import { useMemo, useState, type ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  SectionList,
  Text,
  TextInput,
  View,
  type SectionListRenderItem,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  EquipmentKind,
  MuscleRegion,
  coerceLocale,
  exerciseRegion,
  muscleName,
  pickLocalizedText,
  type EquipmentKind as EquipmentKindT,
  type Exercise,
  type MuscleRegion as MuscleRegionT,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { FilterChipsRow } from "../ExercisePicker/FilterChipsRow";
import { InlineStatusHint } from "../primitives/InlineStatusHint";
import { makeStyles } from "./catalogStyles";
import { CatalogCompactFilterBar } from "./CatalogCompactFilterBar";
import {
  REGION_ORDER,
  computeCatalogSections,
  type Section,
} from "./catalogSections";
import { stateAria } from "../a11y";

// THE Catalog list surface, one tree for mobile's Settings → Catalog
// screen and the web-rnw console. Presentational + self-contained: it owns
// search + region/equipment filters + the per-region collapse state and
// resolves localized names internally. The host injects the catalog rows +
// load status, an optional pull-to-refresh, the pain-dot renderer (native
// only) and the row-select callback (mobile pushes a screen, web fills the
// master-detail pane). Chrome (header / back / SafeAreaView / Edit-on-Web)
// stays in the mount.

export type CatalogListStatus = "loading" | "refreshing" | "error" | "ready";

export type CatalogListViewProps = {
  theme: UiTheme;
  /** Full catalog rows; null = still loading. */
  exercises: Exercise[] | null;
  status: CatalogListStatus;
  /** Load error sentence, already localized by the host; null = none. */
  errorCode: string | null;
  /** Pull-to-refresh; omit (web) to drop the RefreshControl. */
  onRefresh?: () => Promise<unknown> | void;
  /** Slug shown in the detail pane — highlighted in split mode. */
  selectedSlug?: string | null;
  onSelect: (slug: string) => void;
  /** Pain-risk dot rendered before each row name (see bodymap/PainDot.tsx). */
  renderPainDot?: (ex: Exercise) => ReactNode;
  /** What the dot SAYS — a plain function (not a hook) the host builds
   *  once from its own top-level useTranslation()+painMap, since this
   *  row renders via SectionList's renderItem rather than a stable
   *  per-item component (calling a hook there would vary per list
   *  length, unlike ExercisePickerRow's usePainRiskLabel slot). Folded
   *  into the row's own accessibilityLabel — omitting it would let the
   *  dot's own label get swallowed by the row Pressable's, same failure
   *  mode ExerciseSwapPickerRow's header comment documents. */
  getPainRiskLabel?: (ex: Exercise) => string | null;
  /**
   * Dense filter chrome for a narrow pane (the web master-detail list pane):
   * two collapsing "Region ▾ / Equipment ▾" controls + an in-flow option
   * checklist, instead of the full-width horizontal chip rows. The chip rows
   * overflow + can't mouse-scroll in a 380px pane; mobile keeps them.
   */
  compactFilters?: boolean;
};

export function CatalogListView({
  theme,
  exercises: items,
  status,
  errorCode,
  onRefresh,
  selectedSlug,
  onSelect,
  renderPainDot,
  getPainRiskLabel,
  compactFilters = false,
}: CatalogListViewProps) {
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const exName = (name: Exercise["name"]) => pickLocalizedText(name, locale);
  const styles = makeStyles(theme);

  const [query, setQuery] = useState("");
  // Same OR-within-axis / AND-across-axes pattern as the picker. Empty
  // set on either axis means "no constraint on this axis".
  const [regions, setRegions] = useState<Set<MuscleRegionT>>(() => new Set());
  const [equipment, setEquipment] = useState<Set<EquipmentKindT>>(
    () => new Set(),
  );
  // Default state: every region collapsed. When a query is active the
  // collapse state is ignored so matches always show.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(REGION_ORDER),
  );
  const [refreshing, setRefreshing] = useState(false);

  const toggleRegion = (region: MuscleRegionT) =>
    setRegions((prev) => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });

  const toggleEquipment = (kind: EquipmentKindT) =>
    setEquipment((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const clearFilters = () => {
    setRegions(new Set());
    setEquipment(new Set());
    setQuery("");
  };

  const hasFilters = regions.size + equipment.size > 0 || query.length > 0;

  const sections: Section[] = useMemo(
    () =>
      computeCatalogSections({
        items,
        query,
        regions,
        equipment,
        collapsed,
        locale,
      }),
    [items, query, collapsed, regions, equipment, locale],
  );

  const onRefreshInner = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  // Track whether we've ever seen items so the spinner only shows on the
  // very first render — after that, refreshing keeps the cached rows
  // visible (the SWR contract).
  const hasItems = items !== null && items.length > 0;
  const showSpinner = status === "loading" && !hasItems;
  const showError = status === "error" && !hasItems && errorCode !== null;
  const showEmpty =
    !showSpinner && !showError && items !== null && sections.length === 0;

  const renderItem: SectionListRenderItem<Exercise, Section> = ({ item }) => {
    const on = selectedSlug != null && item.slug === selectedSlug;
    // Spelled out instead of left to RN's descendant-text concatenation —
    // renderPainDot's own accessibilityLabel doesn't reliably reach
    // assistive tech once it's nested inside this row's Pressable, same
    // failure mode ExerciseSwapPickerRow's header comment documents.
    const painLabel = getPainRiskLabel?.(item) ?? null;
    const muscle = item.primaryMuscle ? muscleName(item.primaryMuscle, locale) : null;
    const rowLabel = [exName(item.name), painLabel, muscle]
      .filter((part): part is string => typeof part === "string" && part !== "")
      .join(", ");
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={rowLabel}
        accessibilityState={{ selected: on }}
        {...stateAria({ selected: on }, "button")}
        style={({ pressed }) => [
          styles.row,
          on && styles.rowSelected,
          pressed && styles.rowPressed,
        ]}
        onPress={() => onSelect(item.slug)}
      >
        <View style={styles.rowText}>
          <View style={styles.rowNameLine}>
            {renderPainDot?.(item)}
            <Text
              style={[styles.rowName, on && styles.rowNameSelected]}
              numberOfLines={1}
            >
              {exName(item.name)}
            </Text>
          </View>
          {muscle ? (
            <Text style={styles.rowSub} numberOfLines={1}>
              {muscle}
            </Text>
          ) : null}
        </View>
        <Text style={[styles.rowChevron, on && styles.rowChevronSelected]}>
          ›
        </Text>
      </Pressable>
    );
  };

  const renderSectionHeader = ({ section }: { section: Section }) => {
    const filtersActive = hasFilters;
    const isCollapsed = !filtersActive && collapsed.has(section.key);
    const totalForRegion =
      items?.filter((ex) => {
        const r = exerciseRegion(ex);
        return section.key === "other" ? r === null : r === section.key;
      }).length ?? 0;
    const visible = section.data.length;
    const label = t(`catalog.region_${section.key}`, {
      defaultValue: section.key,
    });
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: !isCollapsed }}
        {...stateAria({ expanded: !isCollapsed })}
        onPress={() => {
          if (filtersActive) return;
          setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(section.key)) next.delete(section.key);
            else next.add(section.key);
            return next;
          });
        }}
        style={({ pressed }) => [
          styles.sectionHeader,
          pressed && styles.sectionHeaderPressed,
        ]}
      >
        <Text style={styles.sectionTitle}>{label}</Text>
        <View style={styles.sectionRight}>
          <Text style={styles.sectionCount}>
            {filtersActive ? visible : totalForRegion}
          </Text>
          <Text style={styles.sectionChevron}>{isCollapsed ? "▸" : "▾"}</Text>
        </View>
      </Pressable>
    );
  };

  const refreshingHint =
    status === "refreshing" && hasItems ? (
      <InlineStatusHint
        theme={theme}
        tone="refreshing"
        text={t("mobile.catalog.refreshing", { defaultValue: "Updating…" })}
        size="xs"
        style={styles.refreshingHint}
      />
    ) : null;

  return (
    <View style={styles.flex}>
      <View style={styles.searchWrap}>
        <TextInput
          style={styles.search}
          placeholder={t("mobile.catalog.search")}
          placeholderTextColor={theme.palette.ink3}
          autoCorrect={false}
          autoCapitalize="none"
          value={query}
          onChangeText={setQuery}
        />
      </View>
      {compactFilters ? (
        <CatalogCompactFilterBar
          styles={styles}
          regions={regions}
          equipment={equipment}
          toggleRegion={toggleRegion}
          toggleEquipment={toggleEquipment}
          hasFilters={hasFilters}
          clearFilters={clearFilters}
        />
      ) : (
        <>
          <FilterChipsRow
            theme={theme}
            label={t("catalog.region")}
            options={MuscleRegion.options}
            active={regions}
            onToggle={toggleRegion}
            renderLabel={(region) =>
              t(`catalog.region_${region}`, { defaultValue: region })
            }
          />
          <FilterChipsRow
            theme={theme}
            label={t("catalog.equipment")}
            options={EquipmentKind.options}
            active={equipment}
            onToggle={toggleEquipment}
            renderLabel={(kind) =>
              t(`catalog.equipment_${kind}`, { defaultValue: kind })
            }
          />
          {hasFilters ? (
            <Pressable
              accessibilityRole="button"
              onPress={clearFilters}
              style={({ pressed }) => [
                styles.clearFilters,
                pressed && styles.clearFiltersPressed,
              ]}
            >
              <Text style={styles.clearFiltersText}>
                {t("mobile.logger.pickerClearFilters")}
              </Text>
            </Pressable>
          ) : null}
        </>
      )}
      {refreshingHint}
      {showSpinner ? (
        <View style={styles.center}>
          <ActivityIndicator color={theme.palette.ink2} />
        </View>
      ) : showError ? (
        <View style={styles.center}>
          <Text style={styles.errorText}>{errorCode}</Text>
        </View>
      ) : showEmpty ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>{t("mobile.catalog.empty")}</Text>
        </View>
      ) : (
        <SectionList
          sections={sections}
          keyExtractor={(ex) => ex.slug}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          ItemSeparatorComponent={() => (
            <View
              style={[styles.separator, { backgroundColor: theme.palette.border }]}
            />
          )}
          stickySectionHeadersEnabled={false}
          refreshControl={
            onRefresh ? (
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefreshInner}
                tintColor={theme.palette.ink3}
              />
            ) : undefined
          }
        />
      )}
    </View>
  );
}
